import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import BranchToggle from './BranchToggle';
import { STATUS_CONFIG, KANBAN_COLUMNS, TRANSITIONS } from './utils';
import OrderPopup from './OrderPopup';

export default function KanbanTab({ role }) {
  const [kanban, setKanban] = useState({});
  const [branches, setBranches] = useState([]);
  const [sel, setSel] = useState('');
  const [popupOrder, setPopupOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSA = role === 'SUPER_ADMIN';

  useEffect(() => {
    if (isSA) api.get('/admin/branches').then(r => setBranches(r.data.data||[])).catch(()=>{});
  }, [isSA]);

  const load = useCallback(() => {
    const p = sel ? `?branch_id=${sel}` : '';
    api.get(`/admin/orders/kanban${p}`).then(r => setKanban(r.data.data||{})).catch(()=>{}).finally(() => setLoading(false));
  }, [sel]);

  useEffect(() => { load(); }, [load]);

  const openOrder = async (orderId) => {
    try {
      const r = await api.get(`/admin/orders/${orderId}`);
      setPopupOrder(r.data.data);
    } catch { toast.error('Failed to load order'); }
  };

  const quickTransition = async (orderId, newStatus) => {
    // NEW_ORDER → VERIFICATION_VISIT requires estimated cost — block quick transitions and force popup
    if (newStatus === 'VERIFICATION_VISIT') {
      toast.info('Open the order to enter the estimated cost first.');
      openOrder(orderId);
      return;
    }
    // PENDING_APPROVAL needs validation — also force popup
    if (newStatus === 'PENDING_APPROVAL') {
      toast.info('Open the order to verify required fields before moving to Pending Approval.');
      openOrder(orderId);
      return;
    }
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Moved to ${STATUS_CONFIG[newStatus]?.label||newStatus}`);
      load();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div data-testid="kanban-tab">
      {isSA && branches.length > 0 && (
        <BranchToggle branches={branches} value={sel} onChange={setSel} testIdPrefix="kanban-branch" />
      )}
      <div className="overflow-x-auto">
        <div className="flex gap-3 min-w-max pb-4">
          {KANBAN_COLUMNS.map(status => {
            const cfg = STATUS_CONFIG[status];
            const orders = kanban[status] || [];
            const next = TRANSITIONS[status] || [];
            return (
              <div key={status} className="w-60 flex-shrink-0">
                <div className={`rounded-t-lg px-3 py-2 ${cfg?.color||'bg-muted'}`}>
                  <p className="text-xs font-bold">{cfg?.label||status} ({orders.length})</p>
                </div>
                <div className="bg-muted/30 rounded-b-lg p-2 min-h-[180px] space-y-2">
                  {orders.map(order => (
                    <div key={order.id} className="bg-card rounded-lg p-3 border border-border shadow-sm">
                      <button
                        onClick={() => openOrder(order.id)}
                        className="text-xs font-bold text-primary hover:underline cursor-pointer text-left tabular-nums"
                        data-testid={`kanban-order-${order.id}`}
                      >
                        {order.order_number}
                      </button>
                      <p className="text-[13px] font-medium text-foreground mt-1 leading-tight">{order.customer_name||'Customer'}</p>
                      {order.category_name && <p className="text-[12px] text-primary/80 leading-tight">{order.category_name}</p>}
                      {order.sub_status === 'ALTERATION' && <span className="text-[9px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full font-medium inline-block mt-1">Alteration</span>}
                      {order.total_amount > 0 && <p className="text-[11px] text-muted-foreground mt-1 tabular-nums">Rs. {order.total_amount}</p>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {status === 'READY_FOR_DELIVERY' ? (
                          <>
                            <Button size="sm" className="text-[9px] h-6 gap-0.5 px-2" onClick={() => openOrder(order.id)} data-testid={`kanban-deliver-${order.id}`}>
                              <CheckCircle className="w-2.5 h-2.5" /> Delivered
                            </Button>
                            {['SUPER_ADMIN','BRANCH_ADMIN'].includes(role) && (
                              <Button size="sm" variant="outline" className="text-[9px] h-6 gap-0.5 px-2 text-amber-600 border-amber-300" onClick={() => quickTransition(order.id,'APPROVED')} data-testid={`kanban-alteration-${order.id}`}>
                                <AlertCircle className="w-2.5 h-2.5" /> Alteration
                              </Button>
                            )}
                          </>
                        ) : next.filter(ns => ns!=='CANCELLED' && ns!=='ON_HOLD' && ns!=='APPROVED').slice(0,1).map(ns => (
                          <Button key={ns} size="sm" variant="outline" className="text-[9px] h-6 gap-0.5 px-2" onClick={() => quickTransition(order.id,ns)} data-testid={`kanban-next-${order.id}`}>
                            <ArrowRight className="w-2.5 h-2.5" /> {STATUS_CONFIG[ns]?.label?.split(' ')[0]}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {popupOrder && <OrderPopup order={popupOrder} onClose={() => setPopupOrder(null)} onRefresh={load} role={role} />}
    </div>
  );
}
